<template>
  <section class="jps-item-metatables">
    <h2 class="u-screen-reader-only">
      {{ lss('メタデータ', 'Meta Data') }}
    </h2>
    <table class="jps-meta" aria-live="assertive">
      <tbody>
        <tr
          class="item-field-row"
          v-for="(fl, ind) in filteredLabel"
          :key="ind"
        >
          <th class="is-size-7">
            <span>{{ ls(fl.label) }}</span>
            <span v-if="fl.storeType === 'guionly'">*</span>
          </th>
          <td class="is-size-6">
            <p v-if="fl.indexType === 'uri'">
              <template v-for="(f, k) in getFields(fl.path, item)">
                <a :href="f">{{ f }}</a>
                <br v-if="getFields(fl.path, item).length - 1 != k" />
              </template>
            </p>
            <p
              v-else-if="fl.indexType === 'html'"
              v-shtml="getField(fl.path, item)"
            />
            <p v-else-if="fl.indexType === 'exists'">
              <template v-for="(f, k) in getFields(fl.path, item)">
                <span>
                  {{
                    f ? lss('有り', 'Exists') : lss('無し', 'Not exist')
                  }}</span
                >
                <br v-if="getFields(fl.path, item).length - 1 != k" />
              </template>
            </p>
            <p v-else>{{ getField(fl.path, item) }}</p>
          </td>
        </tr>
        <template v-if="expand">
          <tr class="common-field-row" v-if="item.common.contentsAccess">
            <th class="is-size-7">
              <span>{{ ls(getCommonLabelName('contentsAccess')) }}</span>
            </th>
            <td class="is-size-6">
              {{ lc(codeContentsAccess, item.common.contentsAccess) }}
            </td>
          </tr>
          <tr class="common-field-row" v-if="item.common.contentsType">
            <th class="is-size-7">
              <span>{{ ls(getCommonLabelName('contentsType')) }}</span>
            </th>
            <td class="is-size-6">
              {{ lc(codeDigitalContentsType, item.common.contentsType) }}
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <div v-if="expandable" class="jps-expand-meta text-center">
      <UiButtonLinkRound
        styled="tertiary"
        size="small"
        @click="expand = !expand"
      >
        {{
          expand
            ? ls(dapLangGlobal.hide)
            : lss('すべてのメタデータを表示', 'Show All Metadata')
        }}
      </UiButtonLinkRound>
    </div>
    <div v-if="hasNoAPIField">
      {{ lss('*はAPI利用不可項目', '* can not be retrieved via API') }}
    </div>
  </section>
</template>

<script lang="ts" setup>
import vShtml from '~/directives/shtml'
import { dapLangGlobal } from '#imports'

const { isHina } = useServiceType()
const { ls, lc, lss, ljoin, ljoinNewLine } = useI18n()
const { $getCodeContentsAccess, $getCodeDigitalContentsType, $getCommonLabelDef } = useNuxtApp()

interface Props {
  item: Item
  withrights?: boolean
  rows?: number
  link?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  rows: 7,
  link: false,
  withrights: true,
})

const codeContentsAccess: Code = $getCodeContentsAccess()
const codeDigitalContentsType: Code = $getCodeDigitalContentsType()

const dataStore = useDataStore()

const label = computed(() => {
  return dataStore.label(props.item.common.database)
})

function getCommonLabelName(field: string) {
  return $getCommonLabelDef().filter(l => l.id === field)[0].name
}

const expand: Ref<boolean> = ref(false)

const getField = (path, obj) => {
  if (isHina) {
    return ljoinNewLine(getFields(path, obj)).replace(/\\n|<br\s*\/?>/g, '\n')
  } else {
    return ljoin(getFields(path, obj)).replace(/\\n|<br\s*\/?>/g, '\n')
  }
}

function getFields(path, obj): any[] {
  return deepFind2(obj, path, '-')
}

const validLabels = computed(() => {
  if (!label.value) return []
  return label.value.fieldLabels.filter(
    l =>
      l.storeType !== 'ignore' &&
      !getField(l.path, props.item).match(/^[-－]$/),
  )
})

const descriptionField = computed(() => {
  if (label.value)
    return flatArray(
      label.value.commonLabels
        .filter(
          cl =>
            cl.commonType === 'description' ||
            cl.commonType === 'descriptionEn',
        )
        .map(cl => cl.path),
    )
  else return []
})

const filteredLabel = computed(() => {
  return validLabels.value
    .filter(
      label =>
        expand.value || !descriptionField.value.includes(label.originalPath),
    )
    .filter((l, index) => expand.value || index < props.rows)
})

const expandable = computed(() => {
  return validLabels.value.length > props.rows
})

const hasNoAPIField = computed(() => {
  if (!label.value) return false
  return label.value.fieldLabels.some(l => l.storeType === 'guionly')
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-item-metatables {
  @include global-width-strict();
  .item-field-row + .common-field-row {
    border-top: 2px solid $jps-gray-500;
  }
  .jps-meta {
    width: 100%;
  }
}
</style>
